import { createContext, useContext, useEffect } from "react";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import type { Id } from "convex/_generated/dataModel";
import { BillingWarningBanner } from "@/components/billing";
import { ChatPanelProvider, GlobalChatPanel } from "@/components/ui/ChatPanel";

function getContrastColor(hex: string): string {
  const c = hex.replace("#", "");
  const r = Number.parseInt(c.substring(0, 2), 16);
  const g = Number.parseInt(c.substring(2, 4), 16);
  const b = Number.parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "#000000" : "#ffffff";
}

const ACCENT_VARS = [
  "--color-accent",
  "--color-accent-foreground",
  "--color-accent-soft",
  "--color-accent-medium",
] as const;

function useAccentColor(hex: string | undefined) {
  useEffect(() => {
    const root = document.documentElement;
    if (!hex) return;

    root.style.setProperty("--color-accent", hex);
    root.style.setProperty("--color-accent-foreground", getContrastColor(hex));
    root.style.setProperty("--color-accent-soft", `${hex}1a`);
    root.style.setProperty("--color-accent-medium", `${hex}40`);

    return () => {
      for (const v of ACCENT_VARS) {
        root.style.removeProperty(v);
      }
    };
  }, [hex]);
}

// ── Workspace Context ──────────────────────────────────────────────

type WorkspaceData = {
  _id: Id<"workspaces">;
  name: string;
  slug: string;
  role: string;
  industry: string;
  companySize: string;
  jurisdiction: string;
  accentColor?: string;
};

type WorkspaceCtx = {
  workspace: WorkspaceData;
  workspaceSlug: string;
};

const WorkspaceContext = createContext<WorkspaceCtx | null>(null);

export function useWorkspace(): WorkspaceCtx {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error("useWorkspace must be used within workspace route");
  return ctx;
}

// ── Loading skeleton ───────────────────────────────────────────────

function WorkspaceSkeleton() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="animate-pulse flex flex-col items-center gap-3">
        <div className="h-8 w-48 bg-muted/30 rounded" />
        <div className="h-4 w-32 bg-muted/20 rounded" />
      </div>
    </div>
  );
}

// ── Not found page ─────────────────────────────────────────────────

function WorkspaceNotFound({ slug }: { slug: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4">
      <h1 className="text-xl font-semibold text-primary">
        Workspace not found
      </h1>
      <p className="text-sm text-secondary">
        &ldquo;{slug}&rdquo; doesn&rsquo;t exist or you don&rsquo;t have access.
      </p>
      <Link to="/" className="text-sm text-accent hover:underline">
        Switch Workspace
      </Link>
    </div>
  );
}

// ── Layout component ───────────────────────────────────────────────

function WorkspaceLayout() {
  const { workspaceSlug } = Route.useParams();
  const workspace = useQuery(api.workspaces.getBySlug, {
    slug: workspaceSlug,
  });

  useAccentColor(workspace?.accentColor);

  if (workspace === undefined) return <WorkspaceSkeleton />;
  if (workspace === null) return <WorkspaceNotFound slug={workspaceSlug} />;

  return (
    <WorkspaceContext.Provider
      value={{ workspace: workspace as WorkspaceData, workspaceSlug }}
    >
      <ChatPanelProvider>
        <BillingWarningBanner />
        <Outlet />
        <GlobalChatPanel />
      </ChatPanelProvider>
    </WorkspaceContext.Provider>
  );
}

// ── Route definition ───────────────────────────────────────────────

export const Route = createFileRoute("/$workspaceSlug")({
  component: WorkspaceLayout,
});
